/**
 * Centralized query keys for TanStack Query
 *
 * Using a factory function pattern for type-safe query keys.
 * This ensures consistent key naming and makes invalidation easier.
 */

export const queryKeys = {
    // Categories
    categories: {
        all: ['categories'] as const,
        list: () => [...queryKeys.categories.all, 'list'] as const,
        detail: (id: string) => [...queryKeys.categories.all, id] as const,
    },

    // Locations
    locations: {
        all: ['locations'] as const,
        list: () => [...queryKeys.locations.all, 'list'] as const,
        detail: (id: string) => [...queryKeys.locations.all, id] as const,
    },

    // Products (Equipment Types)
    products: {
        all: ['products'] as const,
        list: (filters?: Record<string, unknown>) =>
            [...queryKeys.products.all, 'list', filters] as const,
        detail: (id: string) => [...queryKeys.products.all, id] as const,
        slug: (slug: string) => [...queryKeys.products.all, 'slug', slug] as const,
    },

    // Availability
    availability: {
        all: ['availability'] as const,
        check: (productId: string, startDate: string, endDate: string) =>
            [...queryKeys.availability.all, productId, startDate, endDate] as const,
    },

    // Accessories
    accessories: {
        all: ['accessories'] as const,
        forProduct: (productId: string) =>
            [...queryKeys.accessories.all, 'product', productId] as const,
    },

    // User
    user: {
        all: ['user'] as const,
        profile: () => [...queryKeys.user.all, 'profile'] as const,
        reservations: () => [...queryKeys.user.all, 'reservations'] as const,
        reservationDetail: (id: string) =>
            [...queryKeys.user.all, 'reservations', id] as const,
    },

    // CMS Page Content — fetched from Backoffice, falls back to defaults when not configured
    pageContent: (locale: string, pageSlug: string, sectionKey: string) =>
        ['pageContent', locale, pageSlug, sectionKey] as const,

    // Auth
    auth: {
        all: ['auth'] as const,
        passwordRequirements: () => [...queryKeys.auth.all, 'password-requirements'] as const,
    },

    // Social Media Accounts
    socialMedia: {
        all: ['socialMedia'] as const,
        active: () => [...queryKeys.socialMedia.all, 'active'] as const,
    },
} as const;

export type QueryKeys = typeof queryKeys;
